import React from 'react';
import { View, Text, StyleSheet, Animated } from 'react-native';
import i18n from 'i18n-js';
import { connect } from 'react-redux';
import moment from 'moment';

import { machineStatusColorName, isPhone, navigateBack, getLinkingModalConfig, isWeb } from '@helpers';

import AnimatedHeader from '@components/common/AnimatedHeader';
import TouchableElement from '@components/common/touchableElement';
import ColorWrapper from '@styles/colors';
import { font as initialFont } from '@styles/fonts';
import Selector from '@styles/selectors';
import Svg from '@styles/icons/svg';
import { scale as initialScale, ratio, notchTop, fullWidth, width } from '@components/frame';

import Timeline from './components/timeline';

import { FeatureFlagsAvailable } from '@components/featureFlags';
import ProductionInformation from '@components/productionInformation';
import Popup from '@components/popup';
import { useRoute } from '@react-navigation/native';
import { MachinesScreen, NowSubScreen, TimePeriod } from '@v2/pages/machines/common/enums';
import { useHandleNavigationMachinesHeader } from '@v2/pages/machines/hooks/useHandleNavigationMachineHeader';
import { useHandleParams } from '@v2/pages/machines/hooks/useHandleParams';
import { setTimePeriod } from '@reducers/params';

const DetailsHeader = ({
  selectedItem = {},
  C,
  setActionsModalOpen,
  showMachineInfo,
  panelViewMode,
  scale = initialScale,
  font = initialFont,
  highlightHeader,
  noMachine,
  user: { featureFlags },
  onBack,
  allSelected,
  onlyTime,
  hideName,
  fadeAnim,
  hideDayActivity,
  setInitialEventDate,
  selectedChartDate,
  setTimePeriod,
}) => {
  const route = useRoute();
  const { isNowSubScreen } = useHandleNavigationMachinesHeader();
  const { timePeriod: selectedTime } = useHandleParams();

  const isNCFileLevel = route.name.includes(NowSubScreen.NC_FILE);
  const isPartLevel = route.name.includes(NowSubScreen.PART);
  const isToolsLevel = route.name.includes(NowSubScreen.TOOLS);

  const HeaderAction = ({ top, zIndex }) => {
    if (noMachine) {
      return selectedItem?.machineStatus?.ncFileId ? (
        <View key={'header-action'} style={{ position: 'absolute', right: scale(18), top: scale(isPhone ? (top ? 18 : 23) : 21), zIndex }}>
          <View style={{ flexDirection: 'row' }}>
            <View style={{ marginLeft: scale(36) }}>
              <TouchableElement
                onPress={() => {
                  Popup({
                    Component: (p) => (
                      <ProductionInformation
                        {...{
                          ...p,
                          onBackPress: p.hide,
                          context: 'batch',
                          selectedNCFileId: selectedItem?.machineStatus?.ncFileId,
                          partId: selectedItem?.machineStatus?.partDetails?.partId,
                        }}
                      />
                    ),
                    ...getLinkingModalConfig(scale, fullWidth),
                  });
                }}
              >
                {({ boxStyle }) => (
                  <View style={[boxStyle]}>
                    <Text style={[font.Body, { color: C('Button/Light/Primary') }]}>{i18n.t('Edit')}</Text>
                  </View>
                )}
              </TouchableElement>
            </View>
          </View>
        </View>
      ) : null;
    }
    return [
      !isPhone && highlightHeader && (
        <View key={'machine-name'} style={{ position: 'absolute', left: scale(10), top: scale(33), zIndex }}>
          <View
            style={{
              backgroundColor: C(machineStatusColorName[selectedItem?.machineStatus?.status]),
              paddingHorizontal: scale(10),
              paddingVertical: scale(5),
              borderRadius: scale(12),
            }}
          >
            <Text style={[font['Title 1'], { color: C('Label/Dark/Primary', false) }]}>{selectedItem?.fullName}</Text>
          </View>
        </View>
      ),
      <View
        key={'header-action'}
        style={{
          position: 'absolute',
          right: scale(18),
          backgroundColor: C('Background/Light/Detail'),
          top: 0,
          paddingTop: scale(isPhone ? 23 : 20),
          zIndex,
        }}
      >
        <View style={{ flexDirection: 'row' }}>
          <TouchableElement onPress={() => setActionsModalOpen(isPhone ? true : 50)}>
            {({ boxStyle }) => (
              <View style={[boxStyle]}>
                <Svg icon="more" width={scale(22)} height={scale(22)} color={'Button/Light/Primary'} />
              </View>
            )}
          </TouchableElement>
          {showMachineInfo && !isPhone && !isToolsLevel && isNowSubScreen && (
            <View style={{ marginLeft: scale(36) }}>
              <TouchableElement
                onPress={() => {
                  Popup({
                    Component: (p) => (
                      <ProductionInformation
                        {...{
                          ...p,
                          onBackPress: p.hide,
                          context: 'batch',
                          selectedNCFileId: selectedItem?.machineStatus?.ncFileId,
                          partId: selectedItem?.machineStatus?.partDetails?.partId,
                        }}
                      />
                    ),
                    ...getLinkingModalConfig(scale, fullWidth),
                  });
                }}
              >
                {({ boxStyle }) => (
                  <View style={[boxStyle]}>
                    <Text style={[font.Body, { color: C('Button/Light/Primary') }]}>{i18n.t('Edit')}</Text>
                  </View>
                )}
              </TouchableElement>
            </View>
          )}
          {panelViewMode && (
            <View style={{ marginLeft: scale(36) }}>
              <TouchableElement
                onPress={() => {
                  if (onBack) {
                    onBack();
                  } else {
                    navigateBack();
                  }
                }}
              >
                {({ boxStyle }) => (
                  <View style={[boxStyle]}>
                    <Text style={[font.Body, { color: C('Button/Light/Primary') }]}>{i18n.t('Close')}</Text>
                  </View>
                )}
              </TouchableElement>
            </View>
          )}
        </View>
      </View>,
    ];
  };

  const PanelHeaderText = () => (
    <View style={{ flex: 1, paddingHorizontal: scale(12), justifyContent: 'center' }}>
      <Text
        style={[
          font['Subhead 2'],
          {
            color: C('Label / Light / Primary'),
            textAlign: 'center',
            marginTop: scale(7),
          },
        ]}
        numberOfLines={1}
      >
        {i18n.t('Production Information')}
      </Text>
      <Text
        style={[
          font['Caption 2'],
          {
            color: C('Label / Light / Primary'),
            textAlign: 'center',
            marginTop: scale(-2),
          },
        ]}
        numberOfLines={1}
        ellipsizeMode="middle"
      >
        {isPartLevel ? selectedItem?.machineStatus?.partDetails?.partName : ''}
        {isNCFileLevel ? selectedItem?.machineStatus?.ncFileName : ''}
      </Text>
    </View>
  );

  const isPhoneMachineList = allSelected && isPhone;

  const levelTabs = [
    {
      name: MachinesScreen.PRODUCTIVE,
    },
    {
      name: MachinesScreen.ACTIVE,
    },
  ];

  const isDay = selectedTime === TimePeriod.DAY;

  const displayOEE = !hideDayActivity && FeatureFlagsAvailable('machine-oee', featureFlags);
  if (displayOEE) {
    levelTabs.unshift({ name: MachinesScreen.UTILIZATION });
  }

  const displayEventList = isDay && !hideDayActivity && !allSelected;
  if (displayEventList) {
    levelTabs.push({ name: MachinesScreen.EVENTS });
  }

  const displayTimeline = (() => {
    if (!isWeb) {
      return false;
    }

    if (selectedTime === TimePeriod.YEAR) {
      return FeatureFlagsAvailable('timeline-year', featureFlags);
    }
    if (selectedTime === TimePeriod.MONTH) {
      return FeatureFlagsAvailable('timeline-month', featureFlags);
    }

    return true;
  })();

  if (displayTimeline) {
    levelTabs.unshift({ name: 'Timeline' });
  }

  const isAdaptiveHeader = width > 1314;

  const startY = isPhone ? 32 : 43;
  const timeY = isPhone ? (isPhoneMachineList ? 83 : 56) : isAdaptiveHeader ? 90 : 65;
  const levelY = isPhone ? (isPhoneMachineList ? 98 : 65) : isAdaptiveHeader ? 90 : 65;

  const RenderTimeline = ({ paddingTop }) => {
    return (
      <Animated.View
        style={[
          {
            alignContent: 'center',
            alignItems: 'center',
            justifyContent: 'center',
            paddingBottom: scale(8),
            opacity: fadeAnim.interpolate({
              inputRange: [scale(timeY), scale(timeY + 1)],
              outputRange: [0, 1],
            }),
            top: fadeAnim.interpolate({
              inputRange: [0, scale(timeY), scale(timeY)],
              outputRange: [-100, -100, 0],
            }),
            backgroundColor: C('Background/Light/Detail'),
            paddingTop: scale(isPhone ? 10 : paddingTop),
          },
          isPhone &&
            !allSelected && {
              borderBottomColor: C('Hairline/Light/Primary'),
              borderBottomWidth: StyleSheet.hairlineWidth,
            },
          isPhoneMachineList && {
            marginTop: scale(-5),
          },
        ]}
      >
        <View
          style={{
            width: isPhone ? '100%' : ratio < 1.5 ? '63%' : '56.5%',
          }}
        >
          <Timeline
            {...{
              setActiveTimeLine: (d) => {
                if (d === selectedTime) {
                  return;
                }

                setTimePeriod(d);

                // if (d !== 'day') {
                //   if (selectedPeriod == chartStatuses.events) {
                //     setSelectedPeriod(chartStatuses.productive);
                //   }
                // }
                // setSelectedTime(d);
              },
              activeTimeLine: selectedTime,
              blur: isPhone,
              colors: { light: '242, 243, 247', dark: '0, 0, 0' },
              scale,
              font,
            }}
          />
        </View>
      </Animated.View>
    );
  };

  const RenderName = () => {
    return (
      <View
        style={{
          flexDirection: 'row',
          paddingTop: scale(15),
          backgroundColor: C('Background/Light/Detail'),
          alignItems: 'center',
        }}
      >
        <View
          style={{
            backgroundColor: selectedItem?.machineStatus?.status ? C(machineStatusColorName[selectedItem.machineStatus.status]) : 'transparent',
            height: scale(12.5),
            width: scale(12.5),
            borderRadius: scale(12.5 / 2),
            opacity: !isNowSubScreen || noMachine || selectedItem?.isProcessingPI || selectedItem?.machineStatisticsOnly ? 0 : 1,
          }}
        />
        <Text
          style={[
            {
              color: C('Label / Light / Primary'),
              fontWeight: 'bold',
              fontSize: scale(20),
              marginLeft: scale(isPhoneMachineList ? 0 : 7),
              letterSpacing: scale(0.01),
            },
            font[isPhoneMachineList ? 'Large Title' : 'Title 2'],
          ]}
          numberOfLines={1}
          ellipsizeMode={'middle'}
        >
          {isPhoneMachineList ? i18n.t('Machines') : selectedItem?.shortName}{' '}
        </Text>
      </View>
    );
  };
  return (
    <Animated.View
      style={{
        width: '100%',
        position: 'absolute',
        zIndex: 10000,
        flex: 1,
        height: fadeAnim.interpolate({
          inputRange: [0, scale(startY), scale(startY)],
          outputRange: [0, 0, scale(50)],
        }),
        backgroundColor: C('Background/Light/Detail'),
      }}
    >
      <Animated.View
        style={{
          opacity: fadeAnim.interpolate({
            inputRange: [scale(startY), scale(startY + 1)],
            outputRange: [0, 1],
          }),
          top: fadeAnim.interpolate({
            inputRange: [0, scale(startY), scale(startY)],
            outputRange: [-100, -100, 0],
          }),
        }}
      >
        {isPhoneMachineList ? (
          <View style={{ height: notchTop + scale(20), paddingTop: scale(12) }}></View>
        ) : (
          <Animated.View style={[{ zIndex: 10000 }]}>
            {isPhone || noMachine ? (
              <AnimatedHeader
                {...{
                  actions: [{ component: HeaderAction, key: 'HeaderAction' }],
                  backText: i18n.t('Back'),
                  onBackPress: onBack || false,
                  text: selectedItem.shortName,
                  C,
                  animated: false,
                  back: true,
                  backgroundColor: 'Background/Light/menuApp',
                  TextComponent: panelViewMode && isPhone ? PanelHeaderText : null,
                  position: 'relative',
                }}
              />
            ) : onlyTime ? null : (
              <HeaderAction />
            )}
          </Animated.View>
        )}
        {!highlightHeader && !noMachine && !isPhone && (
          <View
            style={[
              {
                flexDirection: 'row',
                justifyContent: 'space-between',
                opacity: hideName ? 0 : 1,
              },
              { paddingLeft: scale(isPhoneMachineList ? 6 : 18), paddingRight: scale(18) },
            ]}
          >
            <View
              style={{
                marginTop: scale(isPhone ? 18 : 0),
                paddingRight: isPhone ? scale(75) : 0,
                flex: 1,
              }}
            >
              <View>
                <View style={[isAdaptiveHeader && { position: 'absolute', height: '100%', zIndex: 1000 }]}>
                  <RenderName />
                </View>
                {!noMachine && isAdaptiveHeader && (
                  <View style={{ flex: 1 }}>
                    <RenderTimeline {...{ paddingTop: 15 }} />
                  </View>
                )}
              </View>
            </View>
          </View>
        )}
      </Animated.View>
      {!noMachine && !isAdaptiveHeader && <RenderTimeline />}
      {!noMachine && !isNowSubScreen && (
        <Animated.View
          style={[
            {
              alignContent: 'center',
              alignItems: 'center',
              justifyContent: 'center',
              paddingBottom: scale(8),
              opacity: fadeAnim.interpolate({
                inputRange: [scale(levelY), scale(levelY + 1)],
                outputRange: [0, 1],
              }),
              top: fadeAnim.interpolate({
                inputRange: [0, scale(levelY), scale(levelY)],
                outputRange: [-200, -200, 0],
              }),
              backgroundColor: C('Background/Light/Detail'),
              paddingTop: scale(7),
            },
            isPhone &&
              !allSelected && {
                borderBottomColor: C('Hairline/Light/Primary'),
                borderBottomWidth: StyleSheet.hairlineWidth,
              },
            isPhoneMachineList && {
              marginTop: scale(-5),
            },
          ]}
        >
          <View style={{ flexDirection: 'row' }}>
            {levelTabs.map((item) => (
              <Selector
                key={item.name}
                {...{
                  scale,
                  font,
                  selected: item.name,
                  variant: 'L',
                  text: i18n.t(item.name),
                  onPress: () => {
                    if (item.name === 'Events' && selectedChartDate) {
                      setInitialEventDate(moment(selectedChartDate).endOf('day').utc().format());
                    }
                  },
                }}
              />
            ))}
          </View>
        </Animated.View>
      )}
    </Animated.View>
  );
};

const mapStateToProps = ({ user }) => ({ user });

export default connect(mapStateToProps, { setTimePeriod })(ColorWrapper(DetailsHeader));
